import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Sparkles, Plus, Image as ImageIcon, Loader2, ExternalLink, Trash2, Send,
  Check, AlertTriangle, RotateCcw, FileText, LayoutTemplate, X, Upload, Eye,
  Mic, MicOff, Crop, Repeat, RefreshCw, Minus,
} from 'lucide-react';
import { getAccessToken } from '../services/auth';
import MediaPicker from '../components/MediaPicker';

/**
 * Content Builder — replaces the old Landing Pages builder.
 *
 * Rebecca and Jess brief a page here, Claude builds it from real photos, real
 * reviews and verified facts, it lands as a HIDDEN draft they can preview, and
 * then they keep editing it in plain English (with screenshots) until it is right.
 * They can also pull in any page or blog post that already exists on the site.
 */

const FN = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions'
  : '/.netlify/functions';

async function cb(action, params = {}) {
  const token = getAccessToken();
  const res = await fetch(`${FN}/content-builder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ action, ...params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

const TYPES = [
  { id: 'inspiration', label: 'Landing page', hint: 'Evergreen "how to" page at its own URL. This is what the birthday and reunion pages are.', icon: LayoutTemplate },
  { id: 'blog', label: 'Blog post', hint: 'Dated story that appears in the blog feed. Sticky cover photo on the left.', icon: FileText },
];

/* The blog's sticky LEFT-column photos, for the strip beside "Quick look".
   Greg's call on the 8/18 review: the preview pane shows the RIGHT side of the
   page (header + text + inline photos), so the strip up top answers the one
   question the preview cannot: which 2 or 3 photos are going on the sticky left.
   Order mirrors imageSlots() in lib/content-templates.js — keep in sync. */
function pagePhotos(b) {
  const doc = b.doc;
  if (!doc || typeof doc !== 'object' || doc.kind !== 'blog') return [];
  const out = [];
  const add = (image, label) => {
    if (!image) return;
    const src = image.url
      || (image.key ? `${FN}/r2-resize?key=${encodeURIComponent(image.key)}&edge=240` : '');
    if (src) out.push({ src, label: image.caption ? `${label} — "${image.caption}"` : label });
  };
  add(doc.cover, 'Sticky photo 1 (top of the left column)');
  const extras = (Array.isArray(doc.covers) && doc.covers.length) ? doc.covers : (doc.cover2 ? [doc.cover2] : []);
  extras.slice(0, 2).forEach((c, i) => add(c, `Sticky photo ${i + 2} (left column)`));
  return out;
}

const STATUS_STYLE = {
  draft: { bg: '#eef2f5', fg: '#5a6b76', label: 'Draft' },
  building: { bg: '#FFF1DC', fg: '#DC582A', label: 'Building' },
  preview: { bg: '#e8f1fb', fg: '#00395C', label: 'Preview ready' },
  published: { bg: '#e6f6ec', fg: '#1f7a44', label: 'Live' },
};

// The intake. These are the fields that actually determined quality on the two
// pages we built by hand, in the order it helps to think about them.
const BRIEF_FIELDS = [
  { key: 'occasion', label: 'What is the occasion?', ph: 'Family reunion, graduation, Father\'s Day, game day...', rows: 1,
    help: 'One or two words. It drives the photo search and the review search.' },
  { key: 'angle', label: 'The angle', ph: 'Reunions have one problem: everybody splits into the same little groups. A crab table will not let that happen.', rows: 3,
    help: 'The most important box. What is the ONE idea the page argues? Write it how you would say it out loud.' },
  { key: 'audience', label: 'Who is it for?', ph: 'The person hosting, usually planning for 20 to 40 people', rows: 1 },
  { key: 'mustSay', label: 'Must say', ph: 'Order by the bushel. Engrave mallets with the family name as favors. Reheat in waves for a crowd.', rows: 3,
    help: 'Facts, selling points or details you know matter. One per line.' },
  { key: 'doNotSay', label: 'Do NOT say', ph: 'Do not call it farm raised without explaining why ours is different. Do not promise a birthday reward, that is not live yet.', rows: 2,
    help: 'Just as important as what to say. Claims to avoid, wording you hate, anything not true yet.' },
  { key: 'products', label: 'Products to feature', ph: 'Steamed blue crabs, engraved mallet, Old Bay', rows: 1 },
  { key: 'gear', label: 'Gear section', ph: 'Steamer pot (Amazon), mallets, Old Bay', rows: 1,
    help: 'Three items works best. Say if one should be an Amazon link.' },
  { key: 'photoNotes', label: 'Photo direction', ph: 'Large group shots. Multiple generations. Nothing that is obviously a birthday.', rows: 2,
    help: 'What the pictures should show, and what to avoid.' },
  { key: 'reviewKeyword', label: 'Review keyword', ph: 'reunion', rows: 1,
    help: 'We pull six real 5 star reviews mentioning this word.' },
  { key: 'offer', label: 'Offer, if any', ph: 'Leave blank if there is no offer', rows: 1 },
  { key: 'notes', label: 'Anything else', ph: 'Background, a story, a phrase you want used...', rows: 3 },
];

function Chip({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.draft;
  return <span style={{ background: s.bg, color: s.fg, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: .5 }}>{s.label}</span>;
}

/**
 * Dictation for the "make a change" box.
 *
 * Uses the browser's built-in speech recognition (Chrome and Safari ship it; Firefox
 * does not, so the button hides itself rather than sitting there dead). Needs HTTPS,
 * which we have, and a one-time microphone permission the browser prompts for.
 * Final phrases are appended to whatever is already typed, so talking and typing mix.
 */
function useDictation(onText) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState('');
  const recRef = useRef(null);
  const supported = typeof window !== 'undefined'
    && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const stop = useCallback(() => {
    try { if (recRef.current) recRef.current.stop(); } catch { /* already stopped */ }
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (!supported) return;
    setError('');
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Rec();
    rec.continuous = true;
    rec.interimResults = false;   // only commit finished phrases, so the box does not churn
    rec.lang = 'en-US';
    rec.onresult = (e) => {
      let said = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        if (e.results[i].isFinal) said += e.results[i][0].transcript;
      }
      if (said.trim()) onText(said.trim());
    };
    rec.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        setError('Microphone blocked. Allow the mic for this site in your browser, then try again.');
      } else if (e.error !== 'aborted' && e.error !== 'no-speech') {
        setError('Dictation stopped. You can keep typing.');
      }
      setListening(false);
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch { setListening(false); }
  }, [supported, onText]);

  useEffect(() => () => { try { if (recRef.current) recRef.current.abort(); } catch { /* noop */ } }, []);
  return { supported, listening, error, toggle: () => (listening ? stop() : start()) };
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// ── media picker ─────────────────────────────────────────────────────────────
/* The picker is the SHARED components/MediaPicker (same one every other builder
   uses - full Media Library search, filters and paging). This page used to carry
   its own stripped-down copy with nothing but a text box; Rebecca could not page
   past the first screen or filter at all. Shared picker rows are media_assets
   rows, so adapt them to the {id, key, thumb, alt} shape this page stores. */
const fromAsset = (a) => ({
  id: a.id, key: a.r2_original_key, thumb: a.r2_thumb_key,
  alt: a.alt_text || '', width: a.width, height: a.height,
});

// ── import an existing page/post ─────────────────────────────────────────────
function ImportModal({ onDone, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('');
  useEffect(() => { (async () => {
    try { setData(await cb('bc-list')); } catch (e) { setErr(e.message); }
  })(); }, []);
  const pick = async (row) => {
    setBusy(true); setErr('');
    try { const r = await cb('import', { bc_id: row.bc_id, kind: row.kind }); onDone(r.id); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const f = filter.trim().toLowerCase();
  const all = (data ? [...data.posts, ...data.pages] : [])
    .filter((r) => !f || `${r.name} ${r.url}`.toLowerCase().includes(f));
  return (
    <div className="cb-modal" onClick={onClose}>
      <div className="cb-modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="cb-modal__head">
          <strong>Edit something that is already on the site</strong>
          <button className="btn btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding: '0 16px 12px' }}>
          <input className="form-input" placeholder="Filter by name or url, e.g. birthday" value={filter} onChange={(e) => setFilter(e.target.value)} />
          {data && <p className="cb-help" style={{ marginTop: 6 }}>{all.length} of {data.pages.length + data.posts.length} shown. Blog posts first.</p>}
        </div>
        <div className="cb-modal__body">
          {err && <p className="cb-err">{err}</p>}
          {!data && !err && <p style={{ padding: 16 }}>Loading pages and posts...</p>}
          {all.map((row) => (
            <button key={`${row.kind}-${row.bc_id}`} className="cb-import-row" disabled={busy || row.alreadyHere} onClick={() => pick(row)}>
              <span>
                <strong>{row.name}</strong>
                <em>{row.kind === 'blog' ? 'Blog post' : 'Page'} · {row.url}</em>
              </span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {row.live && <span className="cb-live">LIVE</span>}
                {row.alreadyHere ? <span style={{ fontSize: 12, color: '#7c8894' }}>already added</span> : <Plus size={16} />}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * In-app preview.
 *
 * We cannot iframe the real page: BigCommerce serves crabplace.com with
 * `x-frame-options: deny`, so a cross-origin iframe renders blank (this is what
 * Rebecca hit on the first run). Instead we render the stored HTML directly with
 * srcdoc. Our generated markup carries its own <style> and absolute image URLs, so
 * a landing page comes out close to the real thing. A blog post will be missing the
 * sticky cover, which is theme markup we do not have here, hence the honest label.
 */
function PreviewFrame({ html, interactive, onSelect, onFrame, onText }) {
  // Greg's call on the 8/18 review: the preview shows the RIGHT side of the page
  // (header, text, inline photos) starting at the top — the sticky left-column
  // photos live in the Quick look strip instead, and are managed from the Photos
  // tab. (An earlier version mimicked the theme's two-column layout here; it ate
  // the top of the preview with cover tiles nobody needed twice.)
  const srcDoc = useMemo(() => {
    const hotCss = interactive ? [
      '[data-cb-slot]{cursor:pointer;outline:2px solid transparent;outline-offset:2px;transition:outline-color .12s;}',
      '[data-cb-slot]:hover{outline-color:#DC582A;}',
      '[data-cb-slot].cbp-sel{outline-color:#DC582A;outline-width:3px;}',
      '[data-cb-slot].cbp-adjust{cursor:grab;}',
      '[data-cb-slot].cbp-adjust:active{cursor:grabbing;}',
      '[data-cb-text]{cursor:text;}',
      '[data-cb-text]:hover{outline:1.5px dashed rgba(0,57,92,.45);outline-offset:2px;}',
      '[data-cb-text].cbp-editing{outline:2px solid #00395C;outline-offset:2px;background:rgba(255,255,255,.96);color:#1d1d1f;text-shadow:none;border-radius:4px;}',
    ].join('\n') : '';

    const script = interactive ? [
      '<scr' + 'ipt>(function(){',
      'var sel=null,mode="idle",drag=null,ed=null,edOrig="";',
      'function send(m){parent.postMessage(m,"*");}',
      'function rectOf(el){var r=el.getBoundingClientRect();return {top:r.top,left:r.left,width:r.width,height:r.height};}',
      'function paint(){document.querySelectorAll("[data-cb-slot]").forEach(function(el){',
      '  el.classList.toggle("cbp-sel",el===sel);',
      '  el.classList.toggle("cbp-adjust",el===sel&&mode==="adjust");});}',
      // Click a sentence or heading to edit it in place. Saves on click-away or
      // Enter, Escape cancels. "plaintext-only" keeps existing bold/links intact
      // while pasted junk stays plain; browsers without it fall back to true.
      // Armed on MOUSEDOWN, before the browser applies focus, so the native caret
      // lands exactly where the user clicked and typing just works - the first
      // click-then-focus() version left Rebecca clicking with no way to type.
      // Arming also force-exits photo adjust mode; it used to silently eat text
      // clicks with no way out until you clicked empty space.
      'function endEdit(saveIt){if(!ed)return;var el=ed;ed=null;',
      '  el.removeAttribute("contenteditable");el.classList.remove("cbp-editing");',
      '  if(!saveIt){el.innerHTML=edOrig;return;}',
      '  if(el.innerHTML===edOrig)return;',
      '  send({type:"cb-text",path:el.getAttribute("data-cb-text"),text:el.innerHTML});}',
      'function armEdit(t){if(!t||t===ed)return;',
      '  if(ed)endEdit(true);',
      '  if(mode==="adjust"||sel){mode="idle";sel=null;paint();send({type:"cb-select",path:null});}',
      '  ed=t;edOrig=t.innerHTML;',
      '  t.setAttribute("contenteditable","plaintext-only");',
      '  if(!t.isContentEditable)t.setAttribute("contenteditable","true");',
      '  t.classList.add("cbp-editing");}',
      'document.addEventListener("mousedown",function(e){',
      '  var t=e.target.closest&&e.target.closest("[data-cb-text]");',
      '  if(t)armEdit(t);',
      '},true);',
      'document.addEventListener("click",function(e){',
      '  if(e.target.closest&&e.target.closest("a"))e.preventDefault();',
      '  var t=e.target.closest&&e.target.closest("[data-cb-text]");',
      '  if(ed&&t!==ed)endEdit(true);',
      '  if(t&&t!==ed)armEdit(t);',
      '  if(t&&ed===t&&document.activeElement!==t){try{t.focus({preventScroll:true});}catch(x){t.focus();}}',
      '},true);',
      'document.addEventListener("focusout",function(e){var el=e.target;setTimeout(function(){if(ed===el)endEdit(true);},0);},true);',
      'document.addEventListener("keydown",function(e){if(!ed)return;',
      '  if(e.key==="Escape"){e.preventDefault();endEdit(false);}',
      '  else if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();ed.blur();}},true);',
      'document.addEventListener("click",function(e){',
      '  if(e.target.closest&&e.target.closest("[data-cb-text]"))return;',
      '  var el=e.target.closest&&e.target.closest("[data-cb-slot]");',
      '  if(!el){sel=null;mode="idle";paint();send({type:"cb-select",path:null});return;}',
      '  e.preventDefault();sel=el;paint();',
      '  send({type:"cb-select",path:el.getAttribute("data-cb-slot"),rect:rectOf(el)});},true);',
      'function curFrame(el){',
      '  var isImg=el.tagName==="IMG";',
      '  var pos=(isImg?el.style.objectPosition:el.style.backgroundPosition)||"50% 50%";',
      '  var m=pos.match(/(-?[0-9.]+)%\\s+(-?[0-9.]+)%/);var z=1;',
      '  if(isImg){var t=(el.style.transform||"").match(/scale\\(([0-9.]+)\\)/);if(t)z=parseFloat(t[1]);}',
      '  else{var b=el.style.backgroundSize||"";var bm=b.match(/([0-9.]+)%/);if(bm&&b!=="cover")z=parseFloat(bm[1])/100;}',
      '  return {x:m?parseFloat(m[1]):50,y:m?parseFloat(m[2]):50,z:z||1};}',
      'function applyFrame(el,x,y,z){',
      '  if(el.tagName==="IMG"){el.style.objectPosition=x+"% "+y+"%";el.style.transform=(z===1?"":"scale("+z+")");}',
      '  else{el.style.backgroundPosition=x+"% "+y+"%";el.style.backgroundSize=(z===1?"cover":(100*z)+"% auto");}}',
      'document.addEventListener("pointerdown",function(e){',
      '  if(mode!=="adjust"||!sel)return;',
      '  var el=e.target.closest&&e.target.closest("[data-cb-slot]");if(el!==sel)return;',
      '  e.preventDefault();var f=curFrame(sel),r=sel.getBoundingClientRect();',
      '  drag={px:e.clientX,py:e.clientY,x:f.x,y:f.y,z:f.z,w:r.width,h:r.height};},true);',
      'document.addEventListener("pointermove",function(e){',
      '  if(!drag||!sel)return;',
      '  var nx=Math.max(0,Math.min(100,drag.x-((e.clientX-drag.px)/drag.w)*100));',
      '  var ny=Math.max(0,Math.min(100,drag.y-((e.clientY-drag.py)/drag.h)*100));',
      '  applyFrame(sel,Math.round(nx),Math.round(ny),drag.z);},true);',
      'document.addEventListener("pointerup",function(){',
      '  if(!drag||!sel){drag=null;return;}var f=curFrame(sel);drag=null;',
      '  send({type:"cb-frame",path:sel.getAttribute("data-cb-slot"),focalX:Math.round(f.x),focalY:Math.round(f.y),zoom:f.z});},true);',
      'window.addEventListener("message",function(e){var d=e.data||{};',
      '  if(d.type==="cb-mode"){mode=d.mode;paint();}',
      '  if(d.type==="cb-zoom"&&sel){var f=curFrame(sel);applyFrame(sel,f.x,f.y,d.zoom);',
      '    send({type:"cb-frame",path:sel.getAttribute("data-cb-slot"),focalX:Math.round(f.x),focalY:Math.round(f.y),zoom:d.zoom});}',
      '  if(d.type==="cb-clear"){sel=null;mode="idle";paint();}});',
      '})();</scr' + 'ipt>',
    ].join('\n') : '';

    return [
      '<!doctype html><html><head><meta charset="utf-8">',
      '<meta name="viewport" content="width=device-width,initial-scale=1">',
      '<link rel="preconnect" href="https://fonts.googleapis.com">',
      '<link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&display=swap" rel="stylesheet">',
      '<style>html,body{margin:0;padding:0;background:#fff;font-family:\'Montserrat\',sans-serif;}img,video{max-width:100%;}',
      'body{padding:4px 14px 14px;}',
      hotCss,
      '</style></head><body>',
      html,
      script,
      '</body></html>',
    ].join('\n');
  }, [html, interactive]);

  useEffect(() => {
    if (!interactive) return undefined;
    const onMsg = (e) => {
      const d = e.data || {};
      if (d.type === 'cb-select') onSelect(d.path, d.rect);
      if (d.type === 'cb-frame') onFrame(d.path, { focalX: d.focalX, focalY: d.focalY, zoom: d.zoom });
      if (d.type === 'cb-text' && onText) onText(d.path, d.text);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [interactive, onSelect, onFrame, onText]);

  return (
    <>
      <iframe id="cb-preview" title="preview" srcDoc={srcDoc} className="cb-frame"
        sandbox="allow-same-origin allow-scripts" />
      <p className="cb-help cb-framenote">
        {interactive
          ? 'Click any sentence to edit it in place. Click any photo to swap it, zoom it or take it off. Use Open the real page before you publish.'
          : 'Use Open the real page above to see it inside the real site.'}
      </p>
    </>
  );
}

PreviewFrame.defaultProps = { interactive: false, onSelect: () => {}, onFrame: () => {}, onText: null };

function AdjustPad({ image, onChange }) {
  const padRef = useRef(null);
  const drag = useRef(null);
  const x = Number.isFinite(+image.focalX) ? +image.focalX : 50;
  const y = Number.isFinite(+image.focalY) ? +image.focalY : 50;
  const z = Number.isFinite(+image.zoom) ? +image.zoom : 1;

  const url = image.url || `${FN}/r2-resize?key=${encodeURIComponent(image.key || '')}&edge=900`;
  const padAspect = 16 / 9;
  const ai = image.w && image.h ? image.w / image.h : padAspect;
  const size = z === 1 ? 'cover' : (ai > padAspect ? `auto ${100 * z}%` : `${100 * z}% auto`);

  const onDown = (e) => {
    const r = padRef.current.getBoundingClientRect();
    drag.current = { px: e.clientX, py: e.clientY, x, y, w: r.width, h: r.height };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const d = drag.current;
    // dragging right reveals more of the left edge, so the focal point moves left
    const nx = Math.max(0, Math.min(100, d.x - ((e.clientX - d.px) / d.w) * 100));
    const ny = Math.max(0, Math.min(100, d.y - ((e.clientY - d.py) / d.h) * 100));
    onChange({ focalX: Math.round(nx), focalY: Math.round(ny), zoom: z });
  };
  const onUp = () => { drag.current = null; };

  return (
    <div className="cb-adjust">
      <div
        ref={padRef}
        className="cb-pad"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        style={{ backgroundImage: `url("${url}")`, backgroundPosition: `${x}% ${y}%`, backgroundSize: size }}
        title="Drag the picture to choose what shows"
      />
      <label className="cb-zoomrow">
        <span>Zoom</span>
        <input type="range" min="1" max="3" step="0.05" value={z}
          onChange={(e) => onChange({ focalX: x, focalY: y, zoom: +e.target.value })} />
        <em>{z.toFixed(2)}x</em>
      </label>
      <p className="cb-help" style={{ margin: '2px 0 0' }}>Drag the picture to move it. Nothing here changes the original photo.</p>
    </div>
  );
}

/**
 * The tray: the working set of photos for this page.
 *
 * Rebecca's model is "here are the photos for this article", picked once and then
 * assigned. Without it she was searching the Media Library separately for every slot,
 * which on her reunion page meant thirteen searches instead of one.
 */
/**
 * Blog checklist.
 *
 * Jessica sent the Semrush "anatomy of a perfect blog" sheet. Rather than describe
 * it, the page checks it: title length, web address, the opening, length, alt text,
 * sub-headings, photos, internal and outbound links, a call to action and a wrap-up.
 *
 * Advisory on purpose. Nothing here blocks a publish, because a deliberately short
 * post is not wrong, it just is not the shape the sheet describes.
 */
function BlogChecklist({ briefId, kind, stamp }) {
  const [items, setItems] = useState(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    let live = true;
    cb('check', { id: briefId })
      .then((r) => { if (live) { setItems(r.items || []); setNote(r.note || ''); } })
      .catch(() => { if (live) setItems([]); });
    return () => { live = false; };
  }, [briefId, stamp]);

  if (kind !== 'blog') {
    return (
      <div className="card"><div className="card-body">
        <p className="cb-help" style={{ margin: 0 }}>The checklist is for blog posts. This one is a landing page.</p>
      </div></div>
    );
  }
  if (!items) return <div className="card"><div className="card-body"><Loader2 size={16} className="cb-spin" /></div></div>;

  const done = items.filter((i) => i.ok).length;
  return (
    <div className="card">
      <div className="card-header cb-tray__head">
        <h3>Blog checklist <em>{done} of {items.length}</em></h3>
      </div>
      <div className="card-body">
        {note && <p className="cb-help" style={{ marginTop: 0 }}>{note}</p>}
        <p className="cb-help" style={{ marginTop: 0 }}>
          Nothing here stops you publishing. It is the shape a blog post usually wants,
          so you can see at a glance what is still missing.
        </p>
        <ul className="cb-check">
          {items.map((i) => (
            <li key={i.id} className={i.ok ? 'is-ok' : ''}>
              {i.ok ? <Check size={15} /> : <AlertTriangle size={15} />}
              <b>{i.label}</b>
              {i.hint && <span>{i.hint}</span>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * Layout picker.
 *
 * Jessica's suggestion, almost verbatim: feed it wireframes of different layouts
 * and let them toggle. The wireframes are drawn here rather than shipped as images
 * so they cost nothing and always match what the renderer actually does.
 *
 * Switching a layout never edits the page document. It re-arranges the same words
 * and photos, so trying one on is free and going back is one click.
 */
const WIRE = {
  single: [[8, 6, 44, 18], [8, 27, 44, 4], [8, 34, 34, 3], [8, 40, 44, 3], [8, 47, 30, 3], [8, 56, 44, 12], [8, 71, 30, 3]],
  zigzag: [[8, 6, 26, 16], [37, 9, 19, 4], [37, 16, 19, 3], [8, 27, 19, 4], [8, 34, 19, 3], [30, 26, 26, 16], [8, 47, 26, 16], [37, 50, 19, 4], [37, 57, 19, 3]],
  fpattern: [[8, 5, 44, 5], [8, 13, 36, 3], [8, 21, 14, 11], [25, 22, 27, 4], [25, 29, 20, 3], [8, 36, 14, 11], [25, 37, 27, 4], [25, 44, 20, 3], [8, 51, 14, 11], [25, 52, 27, 4], [25, 59, 20, 3]],
  hybrid: [[8, 5, 44, 15], [8, 23, 30, 4], [8, 30, 44, 3], [8, 39, 18, 13], [29, 40, 23, 4], [29, 47, 23, 3], [8, 55, 18, 13], [29, 56, 23, 4], [29, 63, 23, 3]],
  multi: [[8, 5, 44, 14], [8, 22, 34, 4], [8, 29, 44, 3], [8, 38, 13, 10], [8, 50, 13, 3], [23, 38, 13, 10], [23, 50, 13, 3], [38, 38, 13, 10], [38, 50, 13, 3]],
  double: [[8, 5, 44, 14], [8, 22, 34, 4], [8, 29, 44, 3], [8, 38, 21, 13], [8, 53, 21, 3], [32, 38, 21, 13], [32, 53, 21, 3]],
  perfect: [[8, 5, 44, 5], [8, 14, 20, 3], [8, 19, 20, 3], [8, 24, 20, 3], [8, 29, 16, 3], [32, 14, 20, 15],
    [8, 36, 26, 4], [8, 43, 44, 3], [8, 48, 44, 3], [8, 53, 34, 3], [15, 59, 30, 5], [8, 68, 26, 3], [15, 73, 30, 4]],
  pyramid: [[13, 7, 34, 16], [15, 27, 30, 5], [13, 36, 34, 3], [17, 42, 26, 3], [21, 48, 18, 3], [22, 57, 16, 6]],
};

function Wireframe({ id }) {
  const bars = WIRE[id] || WIRE.single;
  return (
    <svg viewBox="0 0 60 78" className="cb-wire" aria-hidden="true">
      <rect x="0" y="0" width="60" height="78" rx="3" fill="#ffffff" stroke="#e3e8ec" />
      {bars.map((b, i) => (
        <rect key={i} x={b[0]} y={b[1]} width={b[2]} height={b[3]} rx="1"
          fill={b[3] > 5 ? '#c7d4de' : '#00395C'} opacity={b[3] > 5 ? 1 : 0.55} />
      ))}
    </svg>
  );
}

/**
 * Where the photos sit, drawn the same way as the layout wireframes.
 *
 * This is the second half of the answer to Rebecca's 8/19 note. The layouts below
 * arrange the WORDS; this arranges the COLUMN they sit in. Until this existed every
 * layout squeezed the words into the theme's narrow right hand column, so the wide
 * arrangements had nowhere to go.
 */
const PLACE_WIRE = {
  left: [[5, 6, 22, 30], [31, 7, 24, 3], [31, 13, 24, 3], [31, 19, 20, 3], [31, 25, 24, 3], [31, 31, 18, 3]],
  right: [[33, 6, 22, 30], [5, 7, 24, 3], [5, 13, 24, 3], [5, 19, 20, 3], [5, 25, 24, 3], [5, 31, 18, 3]],
  full: [[5, 5, 50, 14], [5, 23, 50, 3], [5, 29, 50, 3], [5, 35, 40, 3]],
};

function PlaceWire({ id }) {
  const bars = PLACE_WIRE[id] || PLACE_WIRE.left;
  return (
    <svg viewBox="0 0 60 42" className="cb-pwire" aria-hidden="true">
      <rect x="0" y="0" width="60" height="42" rx="3" fill="#ffffff" stroke="#e3e8ec" />
      {bars.map((b, i) => (
        <rect key={i} x={b[0]} y={b[1]} width={b[2]} height={b[3]} rx="1"
          fill={b[3] > 5 ? '#c7d4de' : '#00395C'} opacity={b[3] > 5 ? 1 : 0.55} />
      ))}
    </svg>
  );
}

function LayoutPicker({ briefId, kind, current, photoCol, editMode, onChanged }) {
  const [layouts, setLayouts] = useState([]);
  const [places, setPlaces] = useState([]);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    cb('layouts', {}).then((r) => {
      if (!live) return;
      setLayouts(r.layouts || []);
      setPlaces(r.placements || []);
    }).catch(() => {});
    return () => { live = false; };
  }, []);

  if (editMode === 'html') {
    return (
      <div className="card"><div className="card-body">
        <p className="cb-help" style={{ margin: 0 }}>
          This page came in from the website as finished markup, so it keeps the layout it
          already has. Layouts are for pages built here.
        </p>
      </div></div>
    );
  }

  const usable = layouts.filter((l) => (l.appliesTo || []).includes(kind));
  const active = current || (kind === 'blog' ? 'single' : 'zigzag');
  const place = photoCol || 'left';
  const roomy = place === 'full';

  const run = async (key, action, params) => {
    if (busy) return;
    setBusy(key); setErr('');
    try { await cb(action, Object.assign({ id: briefId }, params)); await onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy('');
  };
  // Clicking the choice you are already on REBUILDS the page rather than doing
  // nothing. That is how a published post picks up a rendering fix made after it
  // went out, and it is harmless otherwise: same document, same result.
  const pick = (id) => run(id, 'set-layout', { layout: id });
  const pickPlace = (id) => run('p:' + id, 'set-photo-col', { photoCol: id });

  return (
    <>
      {kind === 'blog' && (
        <div className="card">
          <div className="card-header"><h3>Photos</h3></div>
          <div className="card-body">
            <p className="cb-help" style={{ marginTop: 0 }}>
              Where the main photos sit, and how much room that leaves the words. They can
              stick to the left and follow you down the page, do the same on the right, or
              run across the top as a banner so the words use the whole page.
              <strong> Full width is the one to pick for the roomier layouts below.</strong>
            </p>
            <div className="cb-lays cb-lays--place">
              {places.map((p) => (
                <button key={p.id} className={`cb-lay${p.id === place ? ' is-on' : ''}`}
                  onClick={() => pickPlace(p.id)} disabled={!!busy}>
                  <PlaceWire id={p.id} />
                  <b>{p.name}{p.id === place && <span className="cb-lay__now">in use</span>}</b>
                  <span>{p.blurb}</span>
                  {busy === 'p:' + p.id && <Loader2 size={14} className="cb-spin cb-lay__busy" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header"><h3>Layout</h3></div>
        <div className="card-body">
          <p className="cb-help" style={{ marginTop: 0 }}>
            Pick how the page is arranged. This only moves things around. Your words and
            photos stay exactly as they are, so you can try one on and switch straight back. Clicking the one you are already using rebuilds the page.
            {kind === 'page' && ' On a landing page this arranges the numbered steps.'}
            {kind === 'blog' && !roomy && ' The ones marked below want more room than the side column gives them, so set Photos to Full width first.'}
          </p>
          {err && <p className="cb-err">{err}</p>}
          <div className="cb-lays">
            {usable.map((l) => (
              <button key={l.id} className={`cb-lay${l.id === active ? ' is-on' : ''}`}
                onClick={() => pick(l.id)} disabled={!!busy}>
                <Wireframe id={l.id} />
                <b>{l.name}{l.id === active && <span className="cb-lay__now">in use</span>}</b>
                <span>
                  {l.blurb}
                  {kind === 'blog' && l.needsWidth && !roomy && (
                    <em className="cb-lay__wide">Roomier with Full width photos.</em>
                  )}
                </span>
                {busy === l.id && <Loader2 size={14} className="cb-spin cb-lay__busy" />}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function PhotoTray({ briefId, activePath, onAssign }) {
  const [photos, setPhotos] = useState([]);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);

  const idsRef = useRef([]);

  const load = useCallback(async () => {
    try {
      const rows = (await cb('tray', { id: briefId })).photos || [];
      idsRef.current = rows.map((x) => x.id);
      setPhotos(rows);
    } catch { /* an empty tray is fine */ }
  }, [briefId]);
  useEffect(() => { load(); }, [load]);

  const save = async (ids) => {
    idsRef.current = ids;
    setBusy(true);
    try { await cb('tray-set', { id: briefId, assetIds: ids }); await load(); } catch { /* noop */ }
    setBusy(false);
  };

  return (
    <div className="card cb-tray">
      <div className="card-header cb-tray__head">
        <h3>Photos for this page <em>{photos.length ? `${photos.length} ready` : ''}</em></h3>
        <button className="btn btn-outline" onClick={() => setPicking(true)} disabled={busy}>
          <Plus size={14} /> Add
        </button>
      </div>
      <div className="card-body">
        {!photos.length && (
          <p className="cb-help" style={{ margin: 0 }}>
            Add the photos you want to use, once. Then click a photo on the page and pick from here,
            instead of searching every time.
          </p>
        )}
        {!!photos.length && (
          <>
            {activePath
              ? <p className="cb-help cb-tray__hint">Click one to drop it into the selected photo.</p>
              : <p className="cb-help">Click a photo on the page first, then pick one from here.</p>}
            <div className="cb-tray__grid">
              {photos.map((ph) => (
                <div className="cb-tray__cell" key={ph.id}>
                  <button
                    className={`cb-tray__pic${activePath ? ' is-armed' : ''}`}
                    disabled={!activePath}
                    title={activePath ? 'Use this photo here' : 'Select a photo on the page first'}
                    onClick={() => onAssign(ph.id)}
                  >
                    <img src={`${FN}/r2-image?key=${encodeURIComponent(ph.thumb || ph.key)}`} alt={ph.alt || ''} />
                  </button>
                  <button className="cb-tray__x" title="Take out of the tray"
                    onClick={() => save(photos.filter((x) => x.id !== ph.id).map((x) => x.id))}>
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {picking && (
        <MediaPicker
          onClose={() => setPicking(false)}
          onPick={(r) => { if (!idsRef.current.includes(r.id)) save([...idsRef.current, r.id]); }}
        />
      )}
    </div>
  );
}

function PhotoPanel({ briefId, onChanged }) {
  const [slots, setSlots] = useState(null);
  const [addable, setAddable] = useState([]);
  const [addPath, setAddPath] = useState(null);
  const [unsupported, setUnsupported] = useState('');
  const [openPath, setOpenPath] = useState(null);
  const [swapPath, setSwapPath] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await cb('photos', { id: briefId });
      setSlots(r.slots || []); setAddable(r.addable || []); setUnsupported(r.unsupported || '');
    } catch (e) { setErr(e.message); }
  }, [briefId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const apply = async (path, payload) => {
    setBusy(path); setErr('');
    try {
      const r = await cb('set-photo', { id: briefId, path, ...payload });
      setSlots(r.slots || []);
      onChanged();
    } catch (e) { setErr(e.message); }
    setBusy('');
  };

  // framing drags live locally and save shortly after the user stops moving
  const onFrame = (path, frameVal) => {
    setSlots((prev) => prev.map((sl) => (sl.path === path ? { ...sl, image: { ...sl.image, ...frameVal } } : sl)));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => apply(path, { frame: frameVal }), 600);
  };

  if (unsupported) return <div className="card"><div className="card-body"><p className="cb-help" style={{ margin: 0 }}>{unsupported}</p></div></div>;
  if (!slots) return <div className="card"><div className="card-body"><p>Loading photos...</p></div></div>;

  return (
    <div className="card">
      <div className="card-header"><h3>Photos on this page</h3></div>
      <div className="card-body" style={{ padding: 0 }}>
        {err && <p className="cb-err" style={{ margin: 14 }}>{err}</p>}
        {!slots.length && <p style={{ padding: 18 }} className="cb-help">No photo slots on this page yet.</p>}
        {slots.map((sl) => {
          const img = sl.image || {};
          const thumb = img.url
            || (sl.isVideo && img.poster ? `${FN}/r2-image?key=${encodeURIComponent(img.poster)}`
              : (img.key ? `${FN}/r2-resize?key=${encodeURIComponent(img.key)}&edge=240` : ''));
          const framed = img.focalX != null || img.focalY != null || (img.zoom && img.zoom !== 1);
          return (
            <div className="cb-slot" key={sl.path}>
              <div className="cb-slot__row">
                <span className="cb-slot__thumbwrap">
                  <img className="cb-slot__thumb" src={thumb} alt="" />
                  {sl.isVideo && <span className="cb-slot__vid">VIDEO</span>}
                </span>
                <div className="cb-slot__meta">
                  <strong>{sl.label}</strong>
                  <em>{sl.isVideo ? 'Video clip' : (framed ? 'Reframed' : 'Original framing')}</em>
                </div>
                <div className="cb-slot__acts">
                  <button className="btn btn-outline" disabled={busy === sl.path} onClick={() => setSwapPath(sl.path)} title="Choose a different photo or video">
                    {busy === sl.path ? <Loader2 size={14} className="cb-spin" /> : <Repeat size={14} />} Swap
                  </button>
                  {!sl.isVideo && (
                    <button className={`btn btn-ghost${openPath === sl.path ? ' is-on' : ''}`} onClick={() => setOpenPath(openPath === sl.path ? null : sl.path)} title="Zoom and reposition">
                      <Crop size={14} /> Adjust
                    </button>
                  )}
                  {sl.removable && (
                    <button className="btn btn-ghost cb-slotdel" title="Take this off the page"
                      onClick={() => { if (window.confirm(`Remove "${sl.label}" from the page?`)) apply(sl.path, { remove: true }); }}>
                      <Minus size={14} />
                    </button>
                  )}
                </div>
              </div>
              {sl.captionable !== false && (
                <div className="cb-slot__caprow">
                  <input
                    className="form-input cb-slot__capin"
                    key={`${sl.path}:${sl.caption || ''}`}
                    defaultValue={sl.caption || ''}
                    placeholder="Caption for this photo (optional)"
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    onBlur={(e) => { const v = e.target.value.trim(); if (v !== (sl.caption || '')) apply(sl.path, { caption: v }); }}
                  />
                  {sl.sticky
                    ? <span className="cb-slot__capfix" title="Left-column photos are painted by the site theme, so the caption always sits on the photo">on the photo</span>
                    : sl.isVideo
                      ? <span className="cb-slot__capfix" title="A caption on the video would cover the play controls">under the video</span>
                      : (
                        <select
                          className="form-select cb-slot__cappos"
                          value={sl.captionPos === 'over' ? 'over' : 'under'}
                          title="Where the caption sits"
                          onChange={(e) => apply(sl.path, { captionPos: e.target.value })}
                        >
                          <option value="under">Under the photo</option>
                          <option value="over">On the photo</option>
                        </select>
                      )}
                </div>
              )}
              {openPath === sl.path && !sl.isVideo && (
                <>
                  <AdjustPad image={img} onChange={(f) => onFrame(sl.path, f)} />
                  {framed && (
                    <button className="btn btn-ghost cb-resetframe" onClick={() => apply(sl.path, { reset: true })}>
                      <RefreshCw size={13} /> Reset framing
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })}
        {!!addable.length && (
          <div className="cb-slotadd">
            <p className="cb-help" style={{ margin: '0 0 8px' }}>Spots with no photo or video yet:</p>
            {addable.map((a) => (
              <button key={a.path} className="btn btn-outline" onClick={() => setAddPath(a.path)}>
                <Plus size={13} /> {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {swapPath && (
        <MediaPicker
          onClose={() => setSwapPath(null)}
          onPick={(r) => { const path = swapPath; setSwapPath(null); apply(path, { assetId: r.id }); }}
        />
      )}
      {addPath && (
        <MediaPicker
          onClose={() => setAddPath(null)}
          onPick={(r) => { const path = addPath; setAddPath(null); apply(path, { assetId: r.id }); }}
        />
      )}
    </div>
  );
}

// ── the brief form ───────────────────────────────────────────────────────────
function BriefForm({ value, onChange }) {
  return (
    <>
      {BRIEF_FIELDS.map((f) => (
        <div className="form-group" key={f.key}>
          <label className="form-label">{f.label}</label>
          {f.help && <p className="cb-help">{f.help}</p>}
          {f.rows > 1
            ? <textarea className="form-textarea" rows={f.rows} placeholder={f.ph}
                value={value[f.key] || ''} onChange={(e) => onChange({ ...value, [f.key]: e.target.value })} />
            : <input className="form-input" placeholder={f.ph}
                value={value[f.key] || ''} onChange={(e) => onChange({ ...value, [f.key]: e.target.value })} />}
        </div>
      ))}
    </>
  );
}

export default function ContentBuilder() {
  const [list, setList] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [picking, setPicking] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStep, setJobStep] = useState('');
  const [instruction, setInstruction] = useState('');
  const [shot, setShot] = useState(null);
  const poll = useRef(null);
  const [tab, setTab] = useState('photos');
  const [sel, setSel] = useState(null);          // { path, rect } chosen in the preview
  const [adjusting, setAdjusting] = useState(false);
  const [swapFor, setSwapFor] = useState(null);
  const frameTimer = useRef(null);
  const appendSpoken = useCallback((said) => {
    setInstruction((prev) => (prev ? `${prev.replace(/\s+$/, '')} ${said}` : said));
  }, []);
  const dictation = useDictation(appendSpoken);

  // new-page form state
  const [title, setTitle] = useState('');
  const [type, setType] = useState('inspiration');
  const [placement, setPlacement] = useState('');
  const [brief, setBrief] = useState({});
  const [picked, setPicked] = useState([]);

  const user = useMemo(() => { try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch { return {}; } }, []);

  const loadList = useCallback(async () => {
    try { setList((await cb('list')).rows || []); } catch (e) { setErr(e.message); }
  }, []);
  const loadDetail = useCallback(async (id) => {
    try { setDetail(await cb('get', { id })); } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (openId) loadDetail(openId); }, [openId, loadDetail]);
  useEffect(() => () => clearInterval(poll.current), []);

  // poll a running build/revise job
  useEffect(() => {
    if (!jobId) return undefined;
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const { job } = await cb('job', { jobId });
        if (!job) return;
        setJobStep(job.step || '');
        if (job.status === 'done' || job.status === 'failed') {
          clearInterval(poll.current);
          setJobId(null); setBusy(false);
          if (job.status === 'failed') setErr(job.error || 'Build failed');
          await loadList();
          if (openId) await loadDetail(openId);
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(poll.current);
  }, [jobId, openId, loadDetail, loadList]);

  const startNew = async () => {
    setErr('');
    if (!title.trim()) { setErr('Give it a working title first.'); return; }
    setBusy(true);
    try {
      const { id } = await cb('save-brief', {
        title, content_type: type, placement,
        brief: { ...brief, pickedAssetIds: picked.map((p) => p.id) },
        userName: user.staffName,
      });
      const { jobId: j } = await cb('start-build', { id, userName: user.staffName });
      setCreating(false); setOpenId(id); setJobId(j); setJobStep('Starting...');
      setTitle(''); setBrief({}); setPicked([]); setPlacement('');
      await loadList();
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const sendRevision = async () => {
    if (!instruction.trim() && !shot) { setErr('Tell me what to change, or attach a screenshot.'); return; }
    setErr(''); setBusy(true);
    try {
      let screenshotKey = null;
      if (shot) {
        setJobStep('Uploading screenshot...');
        const up = await fetch(`${FN}/r2-upload`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: `cb-note-${Date.now()}.png`, contentType: shot.type || 'image/png', data: await fileToBase64(shot) }),
        }).then((r) => r.json());
        screenshotKey = up.key;
      }
      const { jobId: j } = await cb('revise', { id: openId, instruction, screenshotKey, userName: user.staffName });
      setInstruction(''); setShot(null); setJobId(j); setJobStep('Starting...');
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const postToFrame = (msg) => {
    const f = document.getElementById('cb-preview');
    if (f && f.contentWindow) f.contentWindow.postMessage(msg, '*');
  };

  // Photo edits never touch the model: they patch one slot and re-render.
  const setPhoto = useCallback(async (path, payload) => {
    try {
      await cb('set-photo', { id: openId, path, ...payload });
      await loadDetail(openId);
    } catch (e) { setErr(e.message); }
  }, [openId, loadDetail]);

  const onPickSlot = useCallback((path, rect) => {
    setSel(path ? { path, rect } : null);
    if (!path) { setAdjusting(false); postToFrame({ type: 'cb-mode', mode: 'idle' }); }
  }, []);

  // Inline copy edits from the preview (click the text, type, click away).
  const setTextSlot = useCallback(async (path, text) => {
    try {
      await cb('set-text', { id: openId, path, text });
      await loadDetail(openId);
    } catch (e) { setErr(e.message); }
  }, [openId, loadDetail]);

  // dragging inside the preview streams values out; save once it settles
  const onFrameChange = useCallback((path, frameVal) => {
    clearTimeout(frameTimer.current);
    frameTimer.current = setTimeout(() => setPhoto(path, { frame: frameVal }), 500);
  }, [setPhoto]);

  useEffect(() => () => clearTimeout(frameTimer.current), []);

  const doPublish = async (publish) => {
    setBusy(true); setErr('');
    try { await cb(publish ? 'publish' : 'unpublish', { id: openId }); await loadDetail(openId); await loadList(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const b = detail && detail.brief;
  const stripPhotos = useMemo(() => (b ? pagePhotos(b) : []), [b]);
  const lint = (b && b.lint) || [];

  // ── list view ──────────────────────────────────────────────────────────────
  if (!openId && !creating) {
    return (
      <div className="page-container">
        <Styles />
        <div className="breadcrumb"><a href="#marketing">Marketing</a><span> / </span>Blog &amp; Page Builder</div>
        <div className="cb-head">
          <div>
            <h1>Blog &amp; Page Builder</h1>
            <p className="cb-sub">Brief a page, Claude builds it from our real photos, real reviews and verified facts, then you keep editing it in plain English until it is right.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline" onClick={() => setImporting(true)}><Upload size={15} /> Edit an existing page</button>
            <button className="btn btn-primary" onClick={() => setCreating(true)}><Plus size={15} /> New page</button>
          </div>
        </div>
        {err && <p className="cb-err">{err}</p>}
        <div className="card"><div className="card-body" style={{ padding: 0 }}>
          {!list.length && <p style={{ padding: 24, color: '#7c8894' }}>Nothing here yet. Start a new page, or pull in one that is already on the site.</p>}
          {list.map((r) => (
            <button key={r.id} className="cb-row" onClick={() => setOpenId(r.id)}>
              <span className="cb-row__main">
                <strong>{r.title}</strong>
                <em>{r.content_type === 'blog' ? 'Blog post' : 'Landing page'}{r.placement ? ` · ${r.placement}` : ''} · /{r.slug}/ · {r.created_by_name || 'unknown'} · {r.updated_label}</em>
              </span>
              <Chip status={r.status} />
            </button>
          ))}
        </div></div>
        {importing && <ImportModal onClose={() => setImporting(false)} onDone={(id) => { setImporting(false); loadList(); setOpenId(id); }} />}
      </div>
    );
  }

  // ── new-brief view ─────────────────────────────────────────────────────────
  if (creating) {
    return (
      <div className="page-container">
        <Styles />
        <div className="breadcrumb"><button className="cb-link" onClick={() => setCreating(false)}>Blog &amp; Page Builder</button><span> / </span>New page</div>
        <h1>Tell me about the page</h1>
        <p className="cb-sub">The more you put in, the closer the first draft lands. Nothing here is permanent, you will be able to change anything afterwards.</p>
        {err && <p className="cb-err">{err}</p>}

        <div className="cb-cols">
          <div className="card"><div className="card-body">
            <div className="form-group">
              <label className="form-label">Working title</label>
              <input className="form-input" placeholder="How to Throw a Graduation Crab Feast" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">What is it?</label>
              <div className="cb-types">
                {TYPES.map((t) => (
                  <button key={t.id} className={`cb-type${type === t.id ? ' is-on' : ''}`} onClick={() => setType(t.id)}>
                    <t.icon size={18} /><strong>{t.label}</strong><em>{t.hint}</em>
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Where does it sit?</label>
              <p className="cb-help">How you would file it. For a landing page this is the hub or occasion; for a blog post it is the topic.</p>
              <input className="form-input" placeholder="Inspiration pages · Reunions" value={placement} onChange={(e) => setPlacement(e.target.value)} />
            </div>
            <BriefForm value={brief} onChange={setBrief} />
          </div></div>

          <div>
            <div className="card"><div className="card-body">
              <label className="form-label">Photos you already know you want</label>
              <p className="cb-help">Optional. Claude will find more, but if you have picks, use them. It checks that a photo actually shows the occasion and avoids filling the page with one family.</p>
              <button className="btn btn-outline" onClick={() => setPicking(true)}><ImageIcon size={15} /> Pick from Media Library</button>
              <div className="cb-picked">
                {picked.map((p) => (
                  <span key={p.id} className="cb-picked__item">
                    <img src={`${FN}/r2-image?key=${encodeURIComponent(p.thumb || p.key)}`} alt="" />
                    <button onClick={() => setPicked(picked.filter((x) => x.id !== p.id))}><X size={12} /></button>
                  </span>
                ))}
              </div>
            </div></div>
            <button className="btn btn-primary cb-build" disabled={busy} onClick={startNew}>
              {busy ? <Loader2 size={16} className="cb-spin" /> : <Sparkles size={16} />} Build the first draft
            </button>
            <p className="cb-help" style={{ textAlign: 'center' }}>Takes a minute or two. It lands hidden, so nothing goes live until you say so.</p>
          </div>
        </div>
        {picking && <MediaPicker imagesOnly onClose={() => setPicking(false)} onPick={(r) => { if (!picked.find((p) => p.id === r.id)) setPicked([...picked, fromAsset(r)]); }} />}
      </div>
    );
  }

  // ── detail / iterate view ──────────────────────────────────────────────────
  return (
    <div className="page-container">
      <Styles />
      <div className="breadcrumb"><button className="cb-link" onClick={() => { setOpenId(null); setDetail(null); }}>Blog &amp; Page Builder</button><span> / </span>{b ? b.title : 'Loading'}</div>
      {!b && <p>Loading...</p>}
      {b && (
        <>
          <div className="cb-head">
            <div>
              <h1>{b.title}</h1>
              <p className="cb-sub">
                {b.content_type === 'blog' ? 'Blog post' : 'Landing page'} · /{b.slug}/ ·{' '}
                {b.edit_mode === 'html' ? 'imported from the site' : 'built here'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Chip status={b.status} />
              {b.preview_url && <a className="btn btn-outline" href={b.preview_url} target="_blank" rel="noreferrer"><Eye size={15} /> Preview</a>}
              {b.status === 'published'
                ? <button className="btn btn-outline" disabled={busy} onClick={() => doPublish(false)}>Unpublish</button>
                : <button className="btn btn-primary" disabled={busy || !b.bc_id} onClick={() => doPublish(true)}><Check size={15} /> Publish</button>}
            </div>
          </div>

          {b.status === 'published' && (
            <p className="cb-warn"><AlertTriangle size={15} /> This page is <strong>live</strong>. Any change you make here goes straight to the website. Every edit is saved so it can be rolled back.</p>
          )}
          {!!lint.length && (
            <div className="cb-warn cb-warn--copy">
              <AlertTriangle size={15} />
              <span><strong>Copy check:</strong> {lint.join(' ')}</span>
            </div>
          )}
          {err && <p className="cb-err">{err}</p>}

          <div className="cb-cols">
            <div className="card">
              <div className="card-header cb-prevhead">
                <h3>Quick look</h3>
                {!!stripPhotos.length && (
                  <div className="cb-prevthumbs" title="These show in the sticky LEFT column of the real page. The preview below is the right side.">
                    <em>Left column:</em>
                    {stripPhotos.map((p, i) => (
                      <button
                        key={`${p.src}-${i}`}
                        type="button"
                        className="cb-prevthumbs__item"
                        title={`${p.label}. Click to manage photos.`}
                        onClick={() => setTab('photos')}
                      >
                        <img src={p.src} alt="" loading="lazy" />
                      </button>
                    ))}
                  </div>
                )}
                {b.preview_url && (
                  <a className="btn btn-primary" href={b.preview_url} target="_blank" rel="noreferrer">
                    <ExternalLink size={15} /> Open the real page
                  </a>
                )}
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {b.html
                  ? (
                    <div className="cb-stage">
                      <PreviewFrame
                        html={b.html}
                        interactive={b.edit_mode !== 'html'}
                        onSelect={onPickSlot}
                        onFrame={onFrameChange}
                        onText={setTextSlot}
                      />
                      {sel && (
                        <div className="cb-hover" style={{ top: Math.max(6, (sel.rect ? sel.rect.top : 0) - 46), left: Math.max(6, sel.rect ? sel.rect.left : 0) }}>
                          <button className="btn btn-navy" onClick={() => setSwapFor(sel.path)}><Repeat size={13} /> Swap</button>
                          <button className={`btn${adjusting ? ' btn-primary' : ' btn-outline'}`}
                            onClick={() => { const n = !adjusting; setAdjusting(n); postToFrame({ type: 'cb-mode', mode: n ? 'adjust' : 'idle' }); }}>
                            <Crop size={13} /> {adjusting ? 'Done' : 'Zoom & move'}
                          </button>
                          {adjusting && (
                            <input className="cb-hover__zoom" type="range" min="1" max="3" step="0.05"
                              defaultValue="1" title="Zoom"
                              onChange={(e) => postToFrame({ type: 'cb-zoom', zoom: +e.target.value })} />
                          )}
                          <button className="btn btn-ghost cb-slotdel" title="Take this photo off the page"
                            onClick={() => { if (window.confirm('Remove this photo from the page?')) { setPhoto(sel.path, { remove: true }); setSel(null); } }}>
                            <Minus size={13} />
                          </button>
                        </div>
                      )}
                      {swapFor && (
                        <MediaPicker
                          onClose={() => setSwapFor(null)}
                          onPick={(r) => { const p = swapFor; setSwapFor(null); setPhoto(p, { assetId: r.id }); }}
                        />
                      )}
                    </div>
                  )
                  : <p style={{ padding: 24 }}>No preview yet.</p>}
              </div>
            </div>

            <div>
              <div className="cb-tabs">
                <button className={`cb-tab${tab === 'photos' ? ' is-on' : ''}`} onClick={() => setTab('photos')}><ImageIcon size={14} /> Photos</button>
                <button className={`cb-tab${tab === 'words' ? ' is-on' : ''}`} onClick={() => setTab('words')}><Send size={14} /> Words</button>
                <button className={`cb-tab${tab === 'layout' ? ' is-on' : ''}`} onClick={() => setTab('layout')}><LayoutTemplate size={14} /> Layout</button>
                {b.content_type === 'blog' && (
                  <button className={`cb-tab${tab === 'check' ? ' is-on' : ''}`} onClick={() => setTab('check')}><Check size={14} /> Checklist</button>
                )}
                <button className={`cb-tab${tab === 'history' ? ' is-on' : ''}`} onClick={() => setTab('history')}><RotateCcw size={14} /> History</button>
              </div>

              {tab === 'photos' && (
                <>
                  <PhotoTray
                    briefId={openId}
                    activePath={sel ? sel.path : null}
                    onAssign={(assetId) => { if (sel) setPhoto(sel.path, { assetId }); }}
                  />
                  <PhotoPanel briefId={openId} onChanged={() => loadDetail(openId)} />
                </>
              )}

              {tab === 'check' && (
                <BlogChecklist briefId={openId} kind={b.content_type === 'blog' ? 'blog' : 'page'} stamp={b.updated_at} />
              )}

              {tab === 'layout' && (
                <LayoutPicker
                  briefId={openId}
                  kind={b.content_type === 'blog' ? 'blog' : 'page'}
                  current={b.doc && b.doc.layout}
                  photoCol={b.doc && b.doc.photoCol}
                  editMode={b.edit_mode}
                  onChanged={() => loadDetail(openId)}
                />
              )}

              {tab === 'words' && (
              <div className="card"><div className="card-header"><h3>Make a change</h3></div><div className="card-body">
                <p className="cb-help">Say it how you would say it to a person. "Rework the intro around the anniversary angle." "Add a section about parking." Tap the mic to talk instead of typing, or attach a screenshot and point at what you mean. <strong>For small wording fixes you do not need this box at all</strong>: click any sentence or heading right on the preview, type, and click away to save. For swapping, captioning or reframing a picture, use the <strong>Photos</strong> tab.</p>
                <div className="cb-talkwrap">
                  <textarea className="form-textarea" rows={4} placeholder="Change the hero photo to one with a longer table..." value={instruction} onChange={(e) => setInstruction(e.target.value)} disabled={!!jobId} />
                  {dictation.supported && (
                    <button
                      type="button"
                      className={`cb-mic${dictation.listening ? ' is-live' : ''}`}
                      onClick={dictation.toggle}
                      disabled={!!jobId}
                      title={dictation.listening ? 'Stop listening' : 'Talk instead of typing'}
                    >
                      {dictation.listening ? <MicOff size={16} /> : <Mic size={16} />}
                    </button>
                  )}
                </div>
                {dictation.listening && <p className="cb-listening">Listening. Just say what you want changed, then press the mic again.</p>}
                {dictation.error && <p className="cb-help" style={{ color: '#a02c1e' }}>{dictation.error}</p>}
                <div className="cb-shotrow">
                  <label className="btn btn-outline cb-shotbtn">
                    <ImageIcon size={15} /> {shot ? shot.name.slice(0, 22) : 'Attach a screenshot'}
                    <input type="file" accept="image/*" hidden onChange={(e) => setShot(e.target.files[0] || null)} />
                  </label>
                  {shot && <button className="btn btn-ghost" onClick={() => setShot(null)}><X size={14} /></button>}
                </div>
                <button className="btn btn-primary cb-build" disabled={!!jobId} onClick={sendRevision}>
                  {jobId ? <Loader2 size={16} className="cb-spin" /> : <Send size={15} />} {jobId ? (jobStep || 'Working...') : 'Apply the change'}
                </button>
              </div></div>

              )}

              {tab === 'history' && !!(detail.revisions || []).length && (
                <div className="card"><div className="card-header"><h3>History</h3></div><div className="card-body" style={{ padding: 0 }}>
                  {detail.revisions.map((r) => (
                    <div key={r.id} className="cb-rev">
                      <span>
                        <strong>{r.instruction || 'Screenshot note'}</strong>
                        <em>{r.created_by_name || 'unknown'} · {r.when_label}</em>
                      </span>
                      <button className="btn btn-ghost" title="Roll back to before this change"
                        onClick={async () => { setBusy(true); try { await cb('revert', { revisionId: r.id }); await loadDetail(openId); } catch (e) { setErr(e.message); } setBusy(false); }}>
                        <RotateCcw size={14} />
                      </button>
                    </div>
                  ))}
                </div></div>
              )}

              <button className="btn btn-ghost cb-danger" onClick={async () => {
                if (!window.confirm('Remove this from the Blog & Page Builder? The page itself stays on the site.')) return;
                await cb('delete', { id: openId }); setOpenId(null); setDetail(null); loadList();
              }}><Trash2 size={14} /> Remove from builder</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Styles() {
  return (
    <style>{`
.cb-head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;flex-wrap:wrap;margin-bottom:18px;}
.cb-sub{color:#7c8894;font-size:14px;max-width:640px;margin:4px 0 0;}
.cb-help{color:#7c8894;font-size:12.5px;margin:0 0 6px;line-height:1.5;}
.cb-err{background:#fdecea;color:#a02c1e;border:1px solid #f5c6c0;border-radius:8px;padding:10px 14px;font-size:14px;margin:0 0 14px;}
.cb-warn{display:flex;gap:8px;align-items:center;background:#FFF6EA;border:1px solid #FCD299;color:#7a5a22;border-radius:8px;padding:10px 14px;font-size:13.5px;margin:0 0 14px;}
.cb-warn--copy{background:#eef4fb;border-color:#cbdcf0;color:#28506f;}
.cb-cols{display:grid;grid-template-columns:1fr;gap:18px;}
@media(min-width:1100px){.cb-cols{grid-template-columns:1.35fr .95fr;align-items:start;}}
.cb-row{display:flex;width:100%;justify-content:space-between;align-items:center;gap:12px;padding:14px 18px;background:none;border:0;border-bottom:1px solid #eef2f5;cursor:pointer;text-align:left;}
.cb-row:hover{background:#fafbfc;}
.cb-row__main{display:flex;flex-direction:column;gap:3px;}
.cb-row__main strong{color:#00395C;font-size:15px;}
.cb-row__main em{color:#7c8894;font-size:12.5px;font-style:normal;}
.cb-link{background:none;border:0;color:#DC582A;cursor:pointer;padding:0;font:inherit;}
.cb-types{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.cb-type{display:flex;flex-direction:column;gap:4px;align-items:flex-start;text-align:left;padding:14px;border:2px solid #e3e8ec;border-radius:10px;background:#fff;cursor:pointer;color:#00395C;}
.cb-type.is-on{border-color:#DC582A;background:#FFF6EA;}
.cb-type em{font-style:normal;font-size:12px;color:#7c8894;line-height:1.45;}
.cb-build{width:100%;justify-content:center;margin-top:12px;}
.cb-spin{animation:cbspin 1s linear infinite;}
@keyframes cbspin{to{transform:rotate(360deg);}}
.cb-frame{width:100%;height:74vh;border:0;border-radius:8px;background:#fff;}
.cb-picked{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px;}
.cb-picked__item{position:relative;width:74px;height:74px;border-radius:8px;overflow:hidden;}
.cb-picked__item img{width:100%;height:100%;object-fit:cover;display:block;}
.cb-picked__item button{position:absolute;top:2px;right:2px;background:rgba(0,0,0,.6);color:#fff;border:0;border-radius:50%;width:18px;height:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;}
.cb-prevhead{display:flex;align-items:center;gap:12px;}
.cb-prevhead h3{margin:0;flex:0 0 auto;}
.cb-prevhead .btn{margin-left:auto;flex:0 0 auto;}
.cb-prevthumbs{display:flex;gap:6px;align-items:center;flex:1;min-width:0;overflow-x:auto;padding:2px 0;}
.cb-prevthumbs em{flex:0 0 auto;font-style:normal;font-size:11px;font-weight:700;letter-spacing:.4px;text-transform:uppercase;color:#7c8894;margin-right:2px;}
.cb-prevthumbs__item{flex:0 0 auto;width:52px;height:52px;border-radius:8px;overflow:hidden;border:1px solid #e3e8ec;padding:0;background:#eef2f5;cursor:pointer;}
.cb-prevthumbs__item img{width:100%;height:100%;object-fit:cover;display:block;}
.cb-slot__thumbwrap{position:relative;display:block;flex:0 0 auto;}
.cb-slot__vid{position:absolute;bottom:3px;left:3px;background:rgba(0,0,0,.72);color:#fff;font-size:9px;font-weight:700;padding:1px 5px;border-radius:3px;letter-spacing:.5px;}
.cb-slot__caprow{display:flex;gap:6px;margin-top:8px;align-items:center;}
.cb-slot__capin{flex:1;min-width:0;font-size:12.5px;padding:7px 10px;}
.cb-slot__cappos{flex:0 0 auto;font-size:12px;padding:6px 6px;border:1px solid #e3e8ec;border-radius:6px;background:#fff;color:#33475b;}
.cb-slot__capfix{flex:0 0 auto;font-size:11px;color:#7c8894;font-style:italic;padding:0 2px;}
.cb-slotadd{padding:12px 14px;border-top:1px solid #eef2f5;display:flex;flex-wrap:wrap;gap:8px;}
.cb-slotadd .cb-help{flex:1 1 100%;}
.cb-slotadd .btn{font-size:12.5px;padding:7px 10px;}
.cb-stage{position:relative;}
.cb-hover{position:absolute;z-index:5;display:flex;gap:6px;align-items:center;background:#fff;border:1px solid #e3e8ec;border-radius:10px;padding:6px;box-shadow:0 6px 20px rgba(0,25,42,.18);}
.cb-hover .btn{padding:6px 10px;font-size:12.5px;}
.cb-hover__zoom{width:110px;}
.cb-tray__head{display:flex;justify-content:space-between;align-items:center;}
.cb-tray__head h3 em{font-style:normal;font-size:12px;color:#7c8894;font-weight:400;margin-left:6px;}
.cb-tray__hint{color:#DC582A !important;font-weight:600;}
.cb-tray__grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(62px,1fr));gap:7px;margin-top:10px;}
.cb-tray__cell{position:relative;}
.cb-tray__pic{padding:0;border:2px solid #e3e8ec;border-radius:7px;overflow:hidden;background:none;cursor:default;width:100%;display:block;}
.cb-tray__pic.is-armed{cursor:pointer;border-color:#FCD299;}
.cb-tray__pic.is-armed:hover{border-color:#DC582A;}
.cb-tray__pic img{width:100%;aspect-ratio:1;object-fit:cover;display:block;}
.cb-tray__x{position:absolute;top:-5px;right:-5px;width:17px;height:17px;border-radius:50%;border:0;background:#00395C;color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;}
.cb-check{list-style:none;margin:0;padding:0;display:grid;gap:7px;}
.cb-check li{display:grid;grid-template-columns:18px 1fr auto;gap:9px;align-items:center;padding:8px 10px;border-radius:8px;background:#fff8f4;border:1px solid #f3ddd0;}
.cb-check li.is-ok{background:#f4faf6;border-color:#dceee2;}
.cb-check li svg{color:#DC582A;}
.cb-check li.is-ok svg{color:#2f9e5f;}
.cb-check li b{font-size:13px;font-weight:600;color:#00395C;}
.cb-check li span{font-size:11.5px;color:#7c8894;white-space:nowrap;}
.cb-lays{display:grid;gap:10px;grid-template-columns:repeat(auto-fill,minmax(184px,1fr));}
.cb-lay{position:relative;display:grid;grid-template-columns:52px 1fr;grid-template-rows:auto auto;gap:2px 11px;align-items:start;text-align:left;padding:11px 12px;border:2px solid #e3e8ec;border-radius:10px;background:#fff;cursor:pointer;}
.cb-lay:hover{border-color:#FCD299;}
.cb-lay.is-on{border-color:#DC582A;background:#fffaf6;}
.cb-lay .cb-wire{grid-row:1 / span 2;width:52px;height:auto;display:block;}
.cb-lay b{font-size:13.5px;color:#00395C;align-self:end;}
.cb-lay span{font-size:11.5px;line-height:1.4;color:#7c8894;}
.cb-lay__now{margin-left:6px;font-size:10px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#DC582A;}
.cb-lay__busy{position:absolute;top:8px;right:8px;color:#DC582A;}
.cb-lays--place{grid-template-columns:repeat(auto-fill,minmax(228px,1fr));}
.cb-lay .cb-pwire{grid-row:1 / span 2;width:60px;height:auto;display:block;}
.cb-lay__wide{display:block;margin-top:4px;font-style:normal;font-weight:600;color:#DC582A;}
.cb-tabs{display:flex;gap:6px;margin:0 0 14px;}
.cb-tab{flex:1;display:flex;align-items:center;justify-content:center;gap:6px;padding:9px 8px;border:1px solid #e3e8ec;background:#fff;border-radius:8px;cursor:pointer;font-size:13px;font-weight:600;color:#5a6b76;}
.cb-tab.is-on{border-color:#DC582A;background:#FFF6EA;color:#00395C;}
.cb-slot{border-bottom:1px solid #eef2f5;padding:12px 16px;}
.cb-slot__row{display:flex;align-items:center;gap:12px;}
.cb-slot__thumb{width:64px;height:48px;object-fit:cover;border-radius:6px;background:#eef2f5;flex:0 0 auto;}
.cb-slot__meta{flex:1;min-width:0;}
.cb-slot__meta strong{display:block;font-size:13.5px;color:#00395C;font-weight:600;line-height:1.3;}
.cb-slot__meta em{font-style:normal;font-size:11.5px;color:#7c8894;}
.cb-slot__acts{display:flex;gap:6px;flex:0 0 auto;}
.cb-slot__acts .btn{padding:6px 10px;font-size:12.5px;}
.cb-slot__acts .btn.is-on{background:#FFF6EA;color:#DC582A;}
.cb-adjust{margin-top:12px;}
.cb-pad{width:100%;aspect-ratio:16 / 9;border-radius:8px;background-color:#eef2f5;background-repeat:no-repeat;cursor:grab;touch-action:none;border:1px solid #e3e8ec;}
.cb-pad:active{cursor:grabbing;}
.cb-zoomrow{display:flex;align-items:center;gap:10px;margin-top:8px;font-size:12.5px;color:#5a6b76;}
.cb-zoomrow input{flex:1;}
.cb-zoomrow em{font-style:normal;color:#00395C;font-weight:700;min-width:44px;text-align:right;}
.cb-slotdel{color:#a02c1e;}
.cb-resetframe{margin-top:6px;font-size:12.5px;color:#7c8894;}
.cb-talkwrap{position:relative;}
.cb-talkwrap .form-textarea{padding-right:46px;}
.cb-mic{position:absolute;top:8px;right:8px;width:34px;height:34px;border-radius:50%;border:1px solid #e3e8ec;background:#fff;color:#00395C;display:flex;align-items:center;justify-content:center;cursor:pointer;}
.cb-mic:hover:not(:disabled){border-color:#DC582A;color:#DC582A;}
.cb-mic:disabled{opacity:.45;cursor:default;}
.cb-mic.is-live{background:#DC582A;border-color:#DC582A;color:#fff;animation:cbpulse 1.4s ease-in-out infinite;}
@keyframes cbpulse{0%,100%{box-shadow:0 0 0 0 rgba(220,88,42,.45);}50%{box-shadow:0 0 0 7px rgba(220,88,42,0);}}
.cb-listening{font-size:12.5px;color:#DC582A;margin:6px 0 0;font-weight:600;}
.cb-shotrow{display:flex;align-items:center;gap:8px;margin-top:10px;}
.cb-shotbtn{cursor:pointer;}
.cb-rev{display:flex;justify-content:space-between;align-items:center;gap:10px;padding:11px 16px;border-bottom:1px solid #eef2f5;}
.cb-rev strong{display:block;font-size:13.5px;color:#00395C;font-weight:600;}
.cb-rev em{font-style:normal;font-size:12px;color:#7c8894;}
.cb-danger{color:#a02c1e;margin-top:12px;}
.cb-modal{position:fixed;inset:0;background:rgba(0,25,42,.55);z-index:9999;display:flex;align-items:center;justify-content:center;padding:24px;}
.cb-modal__panel{background:#fff;border-radius:12px;width:min(940px,100%);max-height:86vh;display:flex;flex-direction:column;overflow:hidden;}
.cb-modal__head{display:flex;justify-content:space-between;align-items:center;padding:16px;border-bottom:1px solid #eef2f5;}
.cb-modal__body{overflow:auto;padding:0 16px 16px;}
.cb-picker-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(130px,1fr));gap:10px;}
.cb-picker-tile{border:2px solid transparent;border-radius:8px;overflow:hidden;background:#f4f7fa;cursor:pointer;padding:0;position:relative;}
.cb-picker-tile:hover{border-color:#DC582A;}
.cb-picker-tile img{width:100%;aspect-ratio:1;object-fit:cover;display:block;}
.cb-picker-tile span{position:absolute;bottom:0;left:0;right:0;background:rgba(0,25,42,.65);color:#fff;font-size:10px;padding:2px;}
.cb-import-row{display:flex;width:100%;justify-content:space-between;align-items:center;gap:12px;padding:12px 8px;background:none;border:0;border-bottom:1px solid #eef2f5;cursor:pointer;text-align:left;}
.cb-import-row:hover:not(:disabled){background:#fafbfc;}
.cb-import-row:disabled{opacity:.5;cursor:default;}
.cb-import-row strong{display:block;color:#00395C;font-size:14px;}
.cb-import-row em{font-style:normal;font-size:12px;color:#7c8894;}
.cb-live{background:#e6f6ec;color:#1f7a44;font-size:10px;font-weight:700;padding:2px 7px;border-radius:20px;}
    `}</style>
  );
}
